import type { ComponentPropsWithoutRef } from 'react';
import { styled } from 'styled-components';
import { Icon, SpaceBetween, tk } from '@sunwebgroup/ui-components';
import { useEnv } from '@/scopes/common/infrastructure/env';
import { useIntl } from '@/intl';
import { buildImgSrc } from '@/helpers/image-url';
import { FooterBlock, FooterBlockHeader } from './Blocks';
import { FooterList, FooterListItem } from './List';
import { useConfiguration } from '@/scopes/common/infrastructure/config/ConfigProvider';

export function Footer({ children, ...attrs }: ComponentPropsWithoutRef<'footer'>) {
  const intl = useIntl();

  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL');
  const links = useConfiguration('links');

  return (
    <StyledFooter {...attrs}>
      {/* This content can differ per page */}
      <StyledContent>{children}</StyledContent>

      <StyledLine />

      <StyledContent>
        <Footer.Block>
          <Footer.Header>{intl.formatMessage({ id: 'footer_download_app_text' })}</Footer.Header>
          <Footer.List direction="x" spacing={tk('spacing_2xs')}>
            <Footer.ListItem href={links.appleStore}>
              <StyledDownloadBadge
                src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_app_store.png`, 200)}
                alt={intl.formatMessage({ id: 'footer_download_app_store' })}
                loading="lazy"
              />
            </Footer.ListItem>
            <Footer.ListItem href={links.googlePlayStore}>
              <StyledDownloadBadge
                src={buildImgSrc(`${mediaServerBaseUrl}/footer/download_play_store.png`, 200)}
                alt={intl.formatMessage({ id: 'footer_download_google_play' })}
                loading="lazy"
              />
            </Footer.ListItem>
          </Footer.List>
        </Footer.Block>
        <SocialsBlock>
          <Footer.Header>{intl.formatMessage({ id: 'footer_us_find_on_social_media' })}</Footer.Header>
          <Footer.List direction="x" spacing={tk('spacing_3xl')}>
            <Footer.ListItem href={links.facebook}>
              <Icon glyph="IconFacebook" />
            </Footer.ListItem>
            <Footer.ListItem href={links.instagram}>
              <Icon glyph="IconInstagram" />
            </Footer.ListItem>
            <Footer.ListItem href={links.youtube}>
              <Icon glyph="IconYoutube" />
            </Footer.ListItem>
          </Footer.List>
        </SocialsBlock>
        <Footer.Block>
          <Footer.Header>{intl.formatMessage({ id: 'footer_affiliated_with' })}</Footer.Header>
          <LogoBlock>
            <LogoLink href={links.affiliates} aria-label={intl.formatMessage({ id: 'footer_link_qualitymarks' })} />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_anvr.png`, 85)}
              alt="Logo ANVR"
              height={28}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_sgr.png`, 85)}
              alt="Logo SGR"
              height={28}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_sgrz.png`, 85)}
              alt="Logo SGRZ"
              height={28}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_calamiteitenfonds.png`, 85)}
              alt="Logo Calamiteitenfonds"
              height={28}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_thuiswinkel.png`, 85)}
              alt="Logo Thuiswinkel Waarborg"
              height={28}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_ecommerce.png`, 85)}
              alt="Logo Trust Ecommerce Europe"
              height={21}
              loading="lazy"
            />
            <StyledLogo
              src={buildImgSrc(`${mediaServerBaseUrl}/footer/logo_travelife.png`, 100)}
              alt="Logo Travelife"
              height={80}
              loading="lazy"
            />
          </LogoBlock>
        </Footer.Block>
      </StyledContent>
    </StyledFooter>
  );
}

Footer.Block = styled(FooterBlock)``;
Footer.Header = styled(FooterBlockHeader)``;
Footer.List = FooterList;
Footer.ListItem = FooterListItem;

const StyledFooter = styled.footer`
  background: ${tk('color_semantic_bg_strong')};
  color: ${tk('color_semantic_text')};
  padding: ${tk('spacing_xl')} ${tk('spacing_md')} 200px;

  @media ${tk('mq_tablet')} {
    padding: ${tk('spacing_3xl')} ${tk('spacing_4xl')} 125px;
  }

  @media ${tk('mq_desktop_lg')} {
    padding: ${tk('spacing_3xl')} ${tk('spacing_4xl')};
  }

  > * {
    margin: 0 auto;
    max-width: ${tk('breakpoint_desktop_xl')};
  }
`;

const StyledContent = styled.div`
  --min-section-size: 275px;
  --gap: ${tk('spacing_3xl')};
  display: grid;
  gap: var(--gap);
  grid-gap: var(--gap);
  grid-template-columns: repeat(auto-fit, minmax(var(--min-section-size), 1fr));

  @media ${tk('mq_tablet')} {
    --gap: ${tk('spacing_xl')};
  }
  @media ${tk('mq_desktop_lg')} {
    grid-template-columns: repeat(3, 1fr);
  }

  /* External links (socials, downloads, logos) */
  &:last-of-type {
    justify-items: center;

    ${Footer.Block}:last-of-type {
      grid-column: 1 / -1;
    }

    @media ${tk('mq_desktop_lg')} {
      justify-items: unset;
      ${Footer.Block}:last-of-type {
        grid-column: auto;
      }
    }
  }
`;

const StyledLine = styled.hr`
  border-color: ${tk('color_semantic_border_strong_hover')};
  margin: ${tk('spacing_lg')} auto;
  @media ${tk('mq_tablet')} {
    margin: ${tk('spacing_2xl')} auto;
  }
`;

const SocialsBlock = styled.section`
  ${Footer.Header} {
    display: none;
  }
  ${Icon} {
    ${Icon.modSize('xl')};
  }

  @media ${tk('mq_tablet')} {
    ${Footer.Header} {
      display: block;
      margin-bottom: ${tk('spacing_lg')};
    }
    ${Footer.List} {
      ${SpaceBetween.modAlignment(tk('spacing_lg'))};
    }
    ${Icon} {
      ${Icon.modSize('md')};
    }
  }
`;

const StyledDownloadBadge = styled.img`
  height: auto;
  max-height: ${tk('spacing_3xl')};
  max-width: 100%;
  width: auto;
`;

const LogoBlock = styled.div`
  --gap: ${tk('spacing_3xl')} ${tk('spacing_md')};
  align-items: center;
  display: grid;
  gap: var(--gap);
  grid-gap: var(--gap);
  grid-template-columns: repeat(6, 28px);
  justify-items: center;
  max-width: max-content;
  position: relative;
  @media ${tk('mq_desktop_sm')} {
    grid-template-columns: repeat(6, 28px) ${tk('spacing_5xl')};
    max-width: unset;
  }
`;

const LogoLink = styled.a`
  height: 100%;
  position: absolute;
  width: 100%;
`;

const StyledLogo = styled.img`
  width: auto;
  ${LogoBlock} > &:last-of-type {
    grid-column: 1 / -1;
    height: ${tk('spacing_5xl')};
    @media ${tk('mq_desktop_sm')} {
      grid-column: auto;
      height: ${tk('spacing_3xl')};
      margin-left: ${tk('spacing_4xl')};
    }
  }
`;
